import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  useTheme,
  alpha,
  Skeleton
} from '@mui/material';
import {
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import MUIStatCard from './ui/MUIStatCard';
import { isTrainerExtendedFeaturesEnabled } from '../utils/featureFlags';

const TrainerPaymentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({
    totalEarnings: 0,
    thisMonthEarnings: 0,
    totalSessions: 0,
    avgHourlyRate: 0,
    pendingPayments: 0
  });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);

  // Redirect trainers if extended features are disabled
  useEffect(() => {
    if (user?.role === 'trainer' && !isTrainerExtendedFeaturesEnabled()) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.role === 'trainer') {
      fetchPaymentData();
    }
  }, [user]);

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch trainer payments
      const paymentsResponse = await api.get('/trainer-payments/my');
      const paymentsData = paymentsResponse.data.data || [];
      
      // Fetch trainer stats
      const statsResponse = await api.get('/trainer-payments/my/stats');
      const statsData = statsResponse.data.data || {};
      
      setPayments(paymentsData);
      setStats(statsData);
      
      // Format chart data
      const chartData = paymentsData.slice(0, 6).map(payment => ({
        period: `${new Date(payment.period.startDate).toLocaleDateString('en-US', { month: 'short' })} ${new Date(payment.period.startDate).getFullYear()}`,
        earnings: payment.summary.trainerEarnings,
        sessions: payment.summary.totalSessions,
        hours: payment.summary.totalHours
      })).reverse();
      
      setChartData(chartData);
      
    } catch (error) {
      setError('Failed to load payment data');
      console.error('Error fetching payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, payment) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayment(payment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPayment(null);
  };

  const handleViewDetails = () => {
    setShowPaymentDetails(true);
    handleMenuClose();
  };

  const handleDownloadInvoice = async () => {
    if (!selectedPayment) return;
    
    try {
      const response = await api.get(`/trainer-payments/${selectedPayment._id}/invoice`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${selectedPayment.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
    
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'approved': return 'info';
      case 'calculated': return 'warning';
      case 'draft': return 'default';
      case 'disputed': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (user?.role !== 'trainer') {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">
          This page is only accessible to trainers.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ 
        mb: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        borderRadius: 3,
        p: 4,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PaymentIcon sx={{ fontSize: 48, mr: 2, color: 'success.main' }} />
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: 'success.main' }}>
              Payment Dashboard
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              Track your earnings and payment history, {user?.firstName}! 💰
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MUIStatCard
            title="Total Earnings"
            value={formatCurrency(stats.totalEarnings)}
            icon={<MoneyIcon />}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MUIStatCard
            title="This Month"
            value={formatCurrency(stats.thisMonthEarnings)}
            icon={<TrendingUpIcon />}
            loading={loading}
            trend="+12% vs last month"
            trendDirection="up"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MUIStatCard
            title="Total Sessions"
            value={stats.totalSessions}
            icon={<ScheduleIcon />}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MUIStatCard
            title="Avg Hourly Rate"
            value={formatCurrency(stats.avgHourlyRate)}
            icon={<PaymentIcon />}
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Earnings Chart */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Earnings Trend (Last 6 Months)
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" width="100%" height={300} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="earnings" fill={theme.palette.success.main} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Session Summary */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Session Summary
              </Typography>
              {loading ? (
                <Box>
                  {[1, 2, 3].map(i => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Skeleton width="100%" height={20} />
                      <Skeleton width="60%" height={16} />
                    </Box>
                  ))}
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="sessions" 
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment History */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                  Payment History
                </Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<DownloadIcon />}
                  onClick={() => {/* Export functionality */}}
                >
                  Export
                </Button>
              </Box>

              {loading ? (
                <Box>
                  {[1, 2, 3, 4, 5].map(i => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 2, borderBottom: 1, borderColor: 'divider' }}>
                      <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="30%" height={20} sx={{ mb: 1 }} />
                        <Skeleton width="20%" height={16} />
                      </Box>
                      <Skeleton width={80} height={24} sx={{ mr: 2 }} />
                      <Skeleton width={100} height={20} sx={{ mr: 2 }} />
                      <Skeleton variant="circular" width={24} height={24} />
                    </Box>
                  ))}
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Period</TableCell>
                        <TableCell>Sessions</TableCell>
                        <TableCell>Hours</TableCell>
                        <TableCell>Earnings</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Invoice</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment._id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {formatDate(payment.period.startDate)} - {formatDate(payment.period.endDate)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {payment.invoiceNumber || 'No invoice'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {payment.summary.totalSessions}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {payment.summary.totalHours.toFixed(1)}h
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} color="success.main">
                              {formatCurrency(payment.summary.trainerEarnings)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={payment.paymentStatus}
                              color={getStatusColor(payment.paymentStatus)}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            {payment.invoiceGenerated ? (
                              <Chip 
                                label="Available"
                                color="success"
                                size="small"
                                variant="outlined"
                                icon={<ReceiptIcon />}
                              />
                            ) : (
                              <Chip 
                                label="Pending"
                                color="default"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton 
                              size="small"
                              onClick={(e) => handleMenuOpen(e, payment)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {!loading && payments.length === 0 && (
                <Box textAlign="center" py={6}>
                  <PaymentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No payment records yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your payment history will appear here once you start conducting sessions.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <ViewIcon sx={{ mr: 1, fontSize: 20 }} />
          View Details
        </MenuItem>
        {selectedPayment?.invoiceGenerated && (
          <MenuItem onClick={handleDownloadInvoice}>
            <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
            Download Invoice
          </MenuItem>
        )}
      </Menu>

      {/* Payment Details Dialog */}
      <Dialog 
        open={showPaymentDetails} 
        onClose={() => setShowPaymentDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Payment Details
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Period
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {formatDate(selectedPayment.period.startDate)} - {formatDate(selectedPayment.period.endDate)}
                  </Typography>
                  
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Status
                  </Typography>
                  <Chip 
                    label={selectedPayment.paymentStatus}
                    color={getStatusColor(selectedPayment.paymentStatus)}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Summary
                  </Typography>
                  <Box>
                    <Typography variant="body2">
                      Total Sessions: {selectedPayment.summary.totalSessions}
                    </Typography>
                    <Typography variant="body2">
                      Total Hours: {selectedPayment.summary.totalHours}h
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      Your Earnings: {formatCurrency(selectedPayment.summary.trainerEarnings)}
                    </Typography>
                  </Box>
                </Grid>

                {/* Session Breakdown */}
                <Grid size={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Session Breakdown
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid size={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="h6" color="primary">
                            {selectedPayment.summary.sessionBreakdown.personalTraining.count}
                          </Typography>
                          <Typography variant="caption">
                            Personal Training
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            {formatCurrency(selectedPayment.summary.sessionBreakdown.personalTraining.trainerEarnings)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid size={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="h6" color="secondary">
                            {selectedPayment.summary.sessionBreakdown.groupClasses.count}
                          </Typography>
                          <Typography variant="caption">
                            Group Classes
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            {formatCurrency(selectedPayment.summary.sessionBreakdown.groupClasses.trainerEarnings)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid size={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="h6" color="info.main">
                            {selectedPayment.summary.sessionBreakdown.consultation.count}
                          </Typography>
                          <Typography variant="caption">
                            Consultations
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            {formatCurrency(selectedPayment.summary.sessionBreakdown.consultation.trainerEarnings)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Payment Details */}
                {selectedPayment.paidAt && (
                  <Grid size={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Payment Information
                    </Typography>
                    <Box>
                      <Typography variant="body2">
                        Paid on: {formatDate(selectedPayment.paidAt)}
                      </Typography>
                      {selectedPayment.paymentMethod && (
                        <Typography variant="body2">
                          Method: {selectedPayment.paymentMethod.replace('_', ' ').toUpperCase()}
                        </Typography>
                      )}
                      {selectedPayment.paymentReference && (
                        <Typography variant="body2">
                          Reference: {selectedPayment.paymentReference}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

                {/* Notes */}
                {selectedPayment.notes && (
                  <Grid size={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Notes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedPayment.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentDetails(false)}>
            Close
          </Button>
          {selectedPayment?.invoiceGenerated && (
            <Button 
              variant="contained" 
              startIcon={<DownloadIcon />}
              onClick={handleDownloadInvoice}
            >
              Download Invoice
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TrainerPaymentDashboard;